<script setup lang="ts">
import { BookOpen, ChevronsUpDown, Library, LogIn } from 'lucide-vue-next'
import Menu from '@/components/FloatMenu/indev.vue'
import AppHeaderMenuUserWithdraw from '@/components/App/Header/Menu/User/Withdraw/index.vue'
import AppHeaderSellerProfileBanner from '@/components/App/Header/Seller/Profile/Banner/index.vue'
import { optimizeImage } from '@/composables/elements.client'

interface Props {
  isOpen: boolean
}

defineProps<Props>()

defineOptions({
  name: 'SidebarLeftFooter',
})

const user = useUser()
const userStripeAccount = useUserStripeAccount()
const featureFlags = useFeatureFlags()
const route = useRoute()

// Get user's first name
function getFirstName(fullName: string | null | undefined): string {
  if (!fullName)
    return 'User'
  return fullName.split(' ')[0]
}

function isActiveRoute(href: string) {
  return route.path === href
}

function logOut() {
  useFirebaseAuth().logOut()
}
</script>

<template>
  <div class="sidebar-footer">
    <div class="footer-content">
      <!-- Knowledge Base & Library Links -->
      <ul class="footer-menu">
        <li class="footer-menu-item">
          <a
            v-if="user"
            href="/library?filter_by=knowledge-base"
            class="footer-menu-button"
            :class="{ 'is-expanded': isOpen, 'is-active': isActiveRoute('/library') && route.query.filter_by === 'knowledge-base' }"
            title="Knowledge Base"
            @click.stop
          >
            <BookOpen :size="24" :class="(isActiveRoute('/library') && route.query.filter_by === 'knowledge-base') ? 'text-foreground' : 'text-muted-foreground'" />
            <span
              v-if="isOpen"
              class="menu-label"
            >Knowledge Base</span>
          </a>
        </li>
        <li class="footer-menu-item">
          <a
            v-if="user"
            href="/library"
            class="footer-menu-button"
            :class="{ 'is-expanded': isOpen, 'is-active': isActiveRoute('/library') && !route.query.filter_by }"
            title="Library"
            @click.stop
          >
            <Library :size="24" :class="(isActiveRoute('/library') && !route.query.filter_by) ? 'text-foreground' : 'text-muted-foreground'" />
            <span
              v-if="isOpen"
              class="menu-label"
            >Library</span>
          </a>
        </li>
      </ul>

      <!-- User Menu -->
      <ul class="footer-menu">
        <li class="footer-menu-item">
          <Menu
            v-if="user"
            position="right"
            container-class="rounded-lg"
            :offset="4"
          >
            <template #default="{ toggle }">
              <button
                type="button"
                class="footer-menu-button user-menu-button"
                :class="{ 'is-expanded': isOpen }"
                title="User Menu"
                @click.stop="toggle()"
              >
                <!-- Avatar -->
                <div class="avatar-wrapper-button">
                  <img
                    v-if="user.avatar"
                    loading="lazy"
                    :src="user.avatar"
                    referrerpolicy="no-referrer"
                    :alt="user.name || 'User'"
                    class="avatar-image"
                  >
                  <div
                    v-else
                    class="avatar-placeholder"
                  >
                    <span class="text-sm font-medium">
                      {{ getFirstName(user.name)?.[0]?.toUpperCase() || 'U' }}
                    </span>
                  </div>
                </div>

                <!-- User info (shown when expanded) -->
                <div
                  v-if="isOpen"
                  class="user-info"
                >
                  <span class="user-name">{{ user.name }}</span>
                  <span class="user-email">{{ user.username }}</span>
                </div>

                <!-- Chevron icon (shown when expanded) -->
                <ChevronsUpDown
                  v-if="isOpen"
                  :size="16"
                  class="chevron-icon ml-auto"
                />
              </button>
            </template>

            <template #options>
              <!-- User info header in dropdown -->
              <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <span class="relative flex shrink-0 overflow-hidden rounded-full w-8 h-8">
                  <img
                    v-if="user.avatar"
                    loading="lazy"
                    :src="optimizeImage(user.avatar, {
                      width: 32,
                      height: 32,
                    })"
                    referrerpolicy="no-referrer"
                    :alt="user.name || 'User'"
                    class="aspect-square w-full h-full object-cover"
                  >
                  <div
                    v-else
                    class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600"
                  >
                    <span class="text-sm font-medium">
                      {{ getFirstName(user.name)?.[0]?.toUpperCase() || 'U' }}
                    </span>
                  </div>
                </span>
                <div class="grid flex-1 text-left text-sm leading-tight">
                  <span class="truncate font-medium">{{ user.name }}</span>
                  <span class="truncate text-xs">{{ user.username }}</span>
                </div>
              </div>

              <!-- Separator -->
              <div class="-mx-1 my-1 bg-border h-px" />

              <!-- Stripe Account / Withdraw (if applicable) -->
              <div v-if="userStripeAccount && userStripeAccount?.state !== 'no account'" class="px-3 pt-2 w-full">
                <AppHeaderSellerProfileBanner v-if="userStripeAccount.state === 'restricted'" />
                <AppHeaderMenuUserWithdraw v-else />
              </div>

              <!-- Profile -->
              <a
                v-if="user"
                :href="`/${user.username}`"
                class="relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none cursor-pointer hover:bg-gray-100 focus:bg-gray-100 transition-colors duration-200"
              >
                <div class="i-lucide:user size-4 shrink-0 text-muted-foreground" />
                <span>Profile</span>
              </a>

              <!-- Settings -->
              <a
                v-if="user"
                :href="`/${user.username}/edit`"
                class="relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none cursor-pointer hover:bg-gray-100 focus:bg-gray-100 transition-colors duration-200"
              >
                <div class="i-lucide:settings size-4 shrink-0 text-muted-foreground" />
                <span>Settings</span>
              </a>

              <!-- API Keys -->
              <a
                v-if="user && featureFlags.apiKeys?.enable"
                href="/api-keys"
                class="relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none cursor-pointer hover:bg-gray-100 focus:bg-gray-100 transition-colors duration-200"
                :class="{ 'bg-gray-100 font-medium': isActiveRoute('/api-keys') }"
              >
                <div class="i-lucide:lock size-4 shrink-0" :class="isActiveRoute('/api-keys') ? 'text-foreground' : 'text-muted-foreground'" />
                <span>API Keys</span>
              </a>

              <!-- Webhooks -->
              <a
                v-if="user"
                href="/webhooks"
                class="relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none cursor-pointer hover:bg-gray-100 focus:bg-gray-100 transition-colors duration-200"
                :class="{ 'bg-gray-100 font-medium': isActiveRoute('/webhooks') }"
              >
                <div class="i-lucide:webhook size-4 shrink-0" :class="isActiveRoute('/webhooks') ? 'text-foreground' : 'text-muted-foreground'" />
                <span>Webhooks</span>
              </a>

              <!-- Workflows -->
              <a
                v-if="user"
                href="/workflows"
                class="relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none cursor-pointer hover:bg-gray-100 focus:bg-gray-100 transition-colors duration-200"
                :class="{ 'bg-gray-100 font-medium': isActiveRoute('/workflows') }"
              >
                <div class="i-lucide:workflow size-4 shrink-0" :class="isActiveRoute('/workflows') ? 'text-foreground' : 'text-muted-foreground'" />
                <span>Workflows</span>
              </a>

              <!-- Separator -->
              <div class="-mx-1 my-1 bg-border h-px" />

              <!-- Logout -->
              <button
                type="button"
                class="relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none cursor-pointer hover:bg-gray-100 focus:bg-gray-100 transition-colors duration-200 w-full text-left"
                @click="logOut"
              >
                <div class="i-lucide:log-out size-4 shrink-0 text-muted-foreground" />
                <span>Sign out</span>
              </button>
            </template>
          </Menu>

          <!-- Login Button if not authenticated -->
          <a
            v-else
            href="/login?platform_redirect=/"
            class="footer-menu-button"
            :class="{ 'is-expanded': isOpen }"
            title="Sign In"
            @click.stop
          >
            <LogIn :size="24" class="shrink-0 text-muted-foreground" />
            <span
              v-if="isOpen"
              class="menu-label"
            >Sign In</span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-footer {
  @apply
    flex
    flex-col
    shrink-0
    mt-auto
    gap-6
    p-3;
}

.footer-content {
  @apply
    flex
    flex-col
    gap-6;
}

.footer-menu {
  @apply
    flex
    w-full
    min-w-0
    flex-col
    gap-6;
}

.footer-menu-item {
  @apply
    relative
}

.footer-menu-button {
  @apply
    flex
    items-center
    justify-center
    overflow-hidden
    rounded-full
    outline-none
    transition-colors
    duration-200
    cursor-pointer
    hover:bg-gray-100
    active:bg-gray-100
    w-12
    h-12
    p-3;

  &.is-expanded {
    @apply
      w-full
      justify-start;
  }

  &.is-active {
    @apply
      bg-gray-100
      font-medium;
  }
}

.user-menu-button {
  @apply
    min-h-[48px]
    flex
    justify-center;
}

.menu-label {
  @apply
    font-bold
    text-base
    truncate
    ml-2;
}

// Avatar styles (for button)
.avatar-wrapper-button {
  @apply
    flex
    justify-center
    items-center
    shrink-0
    rounded-full
    w-6
    h-6;
}

.avatar-image {
  @apply
    rounded-full
    w-full
    h-full
    object-cover
    object-center;
}

.avatar-placeholder {
  @apply
    rounded-full
    bg-gray-200
    flex
    items-center
    justify-center
    text-gray-600
    w-6
    h-6;
}

// User info display (when sidebar is expanded)
.user-info {
  @apply
    grid
    flex-1
    text-left
    text-sm
    leading-tight
    ml-2;
}

.user-name {
  @apply
    truncate
    font-medium;
}

.user-email {
  @apply
    truncate
    text-xs
    text-gray-500;
}

// Chevron icon
.chevron-icon {
  @apply
    ml-auto
    shrink-0;
}
</style>
